import * as React from 'react';
import { Table, Typography } from 'antd';
import { Tree, TreeValidation } from 'tree-wallet-client';
import { formatUTCDateFromDateString } from '../../Helper/DateHelper';

const { Text } = Typography;

// ---------------------------------------------------------------------------------------------------
// Validations table configuration: the numbers its layout is tuned by, to be edited here
// ---------------------------------------------------------------------------------------------------

// The date column shrinks to its content (`1%` is the shrink-to-fit trick with `auto` table layout)
const DATE_COLUMN_WIDTH = '1%';
// Air under the "Validations" title, above the header row
const TITLE_GAP = '12px';
const TITLE_SIZE = '16px';

// Left and right air of the two columns: the Date one, then the Description one. antd's own 16px stays
// on the top and the bottom of every cell
const DATE_COLUMN_PADDING_LEFT = 7;
const DATE_COLUMN_PADDING_RIGHT = 7;
const DESCRIPTION_COLUMN_PADDING_LEFT = 7;
const DESCRIPTION_COLUMN_PADDING_RIGHT = 7;

interface ValidationTableProps {
  tree: Tree;
  // isVisible: boolean;
}

interface ValidationRecord {
  id: number;
  date: string;
  description: string;
}

export default function ValidationTable({ tree }: ValidationTableProps) {
  if (!tree.treeValidations || tree.treeValidations.length === 0) {
    return null;
  }

  // Sort validations by createdAt in descending order (newest first)
  const sortedValidations = [...tree.treeValidations].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const validationRecords: ValidationRecord[] = sortedValidations.map((validation: TreeValidation) => {
    const formattedDate = formatUTCDateFromDateString(validation.createdAt);
    const yesAnswers = [];

    if (validation.leavesFallOff === 'Yes') yesAnswers.push('Leaves fall off');
    if (validation.cutDown === 'Yes') yesAnswers.push('Tree cut down');
    if (validation.eaten === 'Yes') yesAnswers.push('Got eaten');
    if (validation.dugUp === 'Yes') yesAnswers.push('Got dug up');
    if (validation.burned === 'Yes') yesAnswers.push('Got burned');
    if (validation.leavesResprout === 'Yes') yesAnswers.push('Leaves resprout');

    // Build description parts without the date
    const descriptionParts = [`Tree status: ${validation.status}`];

    // Add yes answers if any
    if (yesAnswers.length > 0) {
      descriptionParts.push(yesAnswers.join(', '));
    }

    // Add comment if exists
    if (validation.comment) {
      descriptionParts.push(validation.comment);
    }

    /* if (validation.orientation !== undefined && validation.orientation !== null) {
      descriptionParts.push(`Heading: ${Math.round(validation.orientation)}°`);
    } */

    // Add validator info
    descriptionParts.push(`Validation made by ${validation.validatedBy.firstName} ${validation.validatedBy.lastName}`);

    // Filter out empty parts and join with proper spacing
    const filteredParts = descriptionParts.filter(part => part && part.trim() !== '');
    const description = filteredParts.join(' - ');

    return {
      id: validation.id,
      date: `${formattedDate.date}\n${formattedDate.time}\n(UTC)`,
      description: description
    };
  });

  // The two columns' left/right air, applied to the header cells as well so the titles stay over their
  // values. Only those two sides are set: the top and bottom air is antd's own
  const cellPadding = (paddingLeft: number, paddingRight: number) => ({ style: { paddingLeft, paddingRight } });

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      // As narrow as the date needs: the two lines keep their own breaks and never wrap
      width: DATE_COLUMN_WIDTH,
      onCell: () => cellPadding(DATE_COLUMN_PADDING_LEFT, DATE_COLUMN_PADDING_RIGHT),
      onHeaderCell: () => cellPadding(DATE_COLUMN_PADDING_LEFT, DATE_COLUMN_PADDING_RIGHT),
      render: (text: string) => (
        <div style={{ whiteSpace: 'pre' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      onCell: () => cellPadding(DESCRIPTION_COLUMN_PADDING_LEFT, DESCRIPTION_COLUMN_PADDING_RIGHT),
      onHeaderCell: () => cellPadding(DESCRIPTION_COLUMN_PADDING_LEFT, DESCRIPTION_COLUMN_PADDING_RIGHT),
    }
  ];

  // No top margin: the popup already spaces this row, and the label sits right under it
  return (
    <div>
      <Text strong style={{ fontSize: TITLE_SIZE, display: 'block', marginBottom: TITLE_GAP }}>
        Validations
      </Text>
      <Table
        columns={columns}
        dataSource={validationRecords}
        rowKey="id"
        pagination={false}
      />
    </div>
  );
}
